<!-- 
Component to display one single message
-->
<template>
  <div class="row">
    <div :class="`${cssClass} message-${themeName}`">
      <!-- Deleted message -->
      <div v-if="isDeleted" class="sender d-flex flex-column">
        <div>
          <span class="mr-2">{{ sender }}</span
          ><span class="sentTime">{{ time }} </span>
        </div>
        <div :class="`${isDeleted ? 'deleted-by-sender' : ''}-${themeName}`">
          <v-icon>mdi-cancel</v-icon>
          <span class="ml-3"> {{ type.isSender ? $t('youDeletedMsg') : $t('someoneDeletedMsg') }}</span>
        </div>
      </div>

      <!-- Message -->
      <div v-else class="sender d-flex">
        <span class="mr-2">{{ sender }}</span>
        <span class="sentTime">{{ time }} </span>

        <!-- Messages to read marked with bubble-->
        <span class="unreadBubble ml-1" :class="theme.unreadBadgeColor" v-if="isReceivedMsgUnread"> </span>
        <!-- <span>userId:{{ userId }} isReadStateApplicable:{{ isReadStateApplicable }} isReadByReceiver:{{ isReadByReceiver }}</span> -->

        <!-- Double ticks for messages the receiver has read  -->
        <span v-if="isReadStateApplicable && isReadByReceiver" class="ml-1">
          <v-icon small color="success">mdi-check-all</v-icon>
        </span>
        <!-- Single tick for messages that have been received on the receiver's device but not read -->
        <span v-else-if="isReadStateApplicable && isReceivedConfirmed" class="ml-1"><v-icon small color="success">mdi-check</v-icon></span>

        <!-- Delete message options -->
        <div v-if="type.isSender && !message.deleteMessageForAll" style="position: absolute; right: 20px; top: 0px; width: 20px; height: 20px">
          <v-menu>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item link @click="deleteMessageForAll">
                <v-list-item-title>{{ $t('deleteMessageForAll') }}</v-list-item-title>
              </v-list-item>
              <v-list-item link @click="deleteMessageForMe">
                <v-list-item-title>{{ $t('deleteMessageForMe') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="cancel">
                <v-list-item-title>{{ $t('cancel') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <div class="content pt-1 pb-2">
        <div v-if="!isDeleted" style="white-space: pre-line" :style="{ color: message.deleteMessageForAll ? '#e0e0e0' : null }">{{ text }}</div>
        <div v-if="isDebug && isDeleted" style="white-space: pre-line" :style="{ color: '#a0a0a0' }">{{ text }}</div>
        <div v-if="!isDeleted && isImage" class="msg-img" @click="attachementFullscreenView = true">
          <v-img cover v-if="message.base64Image" :src="message.base64Image"></v-img>
          <v-img cover v-else :src="imageUrl"></v-img>
        </div>

      <div v-if="!isDeleted && isDocument" class="mt-2 mb-3">
        <v-card outlined class="document-container">
          <v-card-text class="d-flex align-center pa-3">
            <v-icon x-large left color="red darken-1">mdi-file-pdf-box</v-icon>
            <div class="file-info">
              <div class="font-weight-bold">{{ message.text || 'Attached Document' }}</div>
            </div>
          </v-card-text>
          <v-card-actions class="pa-2">
            <v-btn
              :href="documentUrl"
              target="_blank"
              color="primary"
              text
              block
            >
              <v-icon left>mdi-download</v-icon>
                 Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>

        <!-- Fullscreen image view -->
        <v-dialog v-if="attachementFullscreenView" v-model="attachementFullscreenView" fullscreen>
          <v-card>
            <v-card-title>
              <v-toolbar flat>
                <v-toolbar-title>
                  {{ message.text ? message.text : '' }}
                  <span class="mr-2 caption">{{ sender }}</span>
                  <span class="sentTime caption">{{ time }} </span>
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                  <v-btn icon @click="attachementFullscreenView = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
            </v-card-title>
            <v-card-text @click="attachementFullscreenView = false">
              <v-img contain v-if="message.base64Image" style="height: calc(100vh - 110px)" :src="message.base64Image"></v-img>
              <v-img contain style="height: calc(100vh - 110px)" v-else :src="imageUrl"></v-img>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!--Debug -->
        <div v-if="isDebug" class="caption">
          userId:{{ userId }} ID:{{ message.id ? message.id : message.clientSideId }} type:{{ type.messageType }}, deletedBySender:{{
            message.deletedBySender
          }}, sender:{{ type.isSender }},receiver:{{ type.isReceiver }},userIdFk {{ message.userIdFk }} senderIdFk: {{ message.senderIdFk }} receiverIdFk:
          {{ message.receiverIdFk }} deleted:
          {{ message.deleted }}
        </div>

        <div v-if="isDebug" class="caption">
          datereceived:{{ message.datereceived }}, dateread: {{ message.dateread }} datecreated: {{ message.datecreated }}
        </div>
        <!-- <div>id: {{message.id}} senderIdFk: {{ message.senderIdFk }}receiverIdFk: {{ message.receiverIdFk }}</div> -->
        <!--End Debug -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import common from '@/mixins/common';
import dateUtils from '@/common/dateUtils';
import moduleId from './moduleId';
import messagingUtil from './messagingUtil';

export default {
  name: 'Message',
  mixins: [common],
  props: {
    message: Object,
    addressee: Object,
  },
  data() {
    return {
      attachementFullscreenView: false,
    };
  },
  mounted() {
    if (!this.isRead) {
      this.markAsReadAfterTimeout();
    }
  },
  computed: {
    ...mapState('messaging', ['userId', 'userById']),
    isDebug() {
      return process.env.NODE_ENV === 'dev';
    },

    text() {
      return this.message.text;
    },
    isSender() {
      return this.type.isSender;
    },
    cssClass() {
      return this.type.isSender ? 'isSender' : 'isReceiver';
    },
    type() {
      return messagingUtil.computeMessageType(this.message, this.userId);
    },
    isDeleted() {
      return this.message.deletedBySender === 1;
    },
    sender() {
      let m = this.message;
      if (m.senderIdFk !== this.userId) {
        let user = this.userById[m.senderIdFk];
        return user ? messagingUtil.getNameFromUserObject(user) : '';
      }
      return this.$t('You');
    },
    time() {
      let date = dateUtils.parseISOString(this.message.datecreated);

      if (date.getHours) {
        return dateUtils.formatAsLocaleShortDateTime(date);
      }
      return '';
    },
    images() {
      return this.message.images;
    },
    // read state to display only when a message is sent by current user in a private chat
    isReadStateApplicable() {
      let m = this.message;
      if (m.isGroup) return false;
      return m.senderIdFk === this.userId;
    },
    isReceivedConfirmed() {
      return !!this.message.datereceived;
    },
    isRead() {
      return !messagingUtil.isNotRead(this.message, this.userId);
    },
    isReceivedMsgUnread() {
      return !this.isSender && messagingUtil.isUnread(this.message, this.userId);
    },
    isReadByReceiver() {
      let m = this.message;
      if (m.isGroup) return null;
      return m.dateread && m.userIdFk === m.receiverIdFk;
    },
    isImage() {
      return this.message.type === 'image';
    },
    imageUrl() {
      if (this.isImage) {
        return this.moduleFileUrl(moduleId, this.message.filename, this.message.checksum);
      }
      return null;
    },
    /**
     * Checks if the message is a document.
     */
    isDocument() {
      return this.message.type === 'document';
    },

    /**
     * Builds the download URL for the document.
     * It uses the same logic and mixin as imageUrl.
     */
    documentUrl() {
      if (this.isDocument) {
        return this.moduleFileUrl(moduleId, this.message.filename, this.message.checksum);
      }
      return null;
    },
  },
  methods: {
    // ...mapMutations('messaging', ['markMessageAsRead']),
    ...mapActions('messaging', ['deleteMessage', 'markMessageAsRead']),

    cancel() {},

    deleteMessageForMe() {
      this.deleteMessage({ message: this.message, mode: 'deleteMessageForMe' });
    },
    deleteMessageForAll() {
      this.deleteMessage({ message: this.message, mode: 'deleteMessageForAll' });
    },
    markAsReadAfterTimeout() {
      setTimeout(() => {
        console.debug('mark as read', this.message);
        this.markMessageAsRead(this.message);
      }, 3000);
    },
  },
};
</script>
<style scoped>
.row {
  display: flex;
  width: 100%;
}

.message-light {
  background: white;
  border: 1px solid #b0b0b0;
}

.message-dark {
  background: #303030;
  border: 1px solid #353535;
}

.message-light,
.message-dark {
  border-radius: 5px;
  position: relative;
  margin: 4px;
  padding-top: 10px;
  padding-left: 20px;
  padding-right: 40px;
  max-width: 90%;
  font-size: 0.95rem;
}

.deleted-by-sender-light > * {
  color: #d0d0d0 !important;
}

.deleted-by-sender-dark > * {
  color: #808080 !important;
}

.isSender {
  margin-left: auto;
}

.isReceiver {
  justify-content: flex-start;
}

.sender {
  font-size: 0.8rem;
  font-weight: bold;
}

.msg-img {
  border-radius: 6px;
  overflow: hidden;
  max-width: 500px;
}

.document-container {
  max-width: 600px;
  border-radius: 8px;
  background-color: rgba(0,0,0,0.02); /* Faint background */
}

.file-info {
  /* Prevents long text from breaking the layout */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 8px;
}

.unreadBubble {
  border-radius: 4px;
  width: 8px;
  height: 8px;
}
</style>
